import React, { useEffect, useState } from 'react';
import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiPanel, EuiSideNav, EuiText } from '@elastic/eui';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import SongManagement from './SongManagement';
import UserManagement from './UserManagement';
import RadioManagement from './RadioPageWithPlayer';
import VerseManagement from './VerseManagement';
import BibleCuvManagement from './BibleCuvManagement';
import ReadingPlanManagement from './ReadingPlanManagement';
import ReadingPlanTemplateManagement from './ReadingPlanTemplateManagement';
import SermonManagement from './SermonManagement';
import NetworkManagement from './NetworkManagement';
import PrayManagement from './PrayManagement';
import CommentManagement from './CommentManagement';
import DataAnalysis from './DataAnalysis';
import LanguageToggle from './LanguageToggle';
import { TranslateLanguage } from './KeywordTranslator';
import AdminLanguageBoundary from './AdminLanguageBoundary';
import EmailBroadcastManagement from './EmailBroadcastManagement';
import '../admin-data-management.css';

const managementPages = [
  { name: '用戶管理', id: 'users' },
  { name: '詩歌管理', id: 'songs' },
  { name: '電台管理', id: 'radio' },
  { name: 'Elastic 經文管理', id: 'scripture' },
  { name: 'MySQL 經文管理', id: 'mysql-scripture' },
  { name: '讀經計劃管理', id: 'reading-plans' },
  { name: '讀經模板管理', id: 'reading-plan-templates' },
  { name: '信息管理', id: 'info' },
  { name: '網路管理', id: 'network' },
  { name: '禱告管理', id: 'prayer' },
  { name: '註解管理', id: 'comment' },
  { name: '數據分析', id: 'analysis' },
  { name: '群發郵件', id: 'email-broadcasts' },
];
const selectedPageStorageKey = 'ctb-admin-selected-page';

const AdminDashboard = () => {
  const [selectedPage, setSelectedPage] = useState(() => {
    const savedPage = sessionStorage.getItem(selectedPageStorageKey);
    return managementPages.some(item => item.id === savedPage) ? savedPage : 'users';
  });
  const [isAdmin, setIsAdmin] = useState(null);
  const navigate = useNavigate();
  const language = localStorage.userLanguage || ((navigator.language || navigator.userLanguage) === 'zh-CN' ? 'S' : 'T');
  const translate = text => TranslateLanguage(language, text);
  const selectPage = id => {
    sessionStorage.setItem(selectedPageStorageKey, id);
    setSelectedPage(id);
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) { setIsAdmin(false); return; }
    try { setIsAdmin(jwtDecode(token).role === 'Admin'); }
    catch (error) { console.error('Invalid admin token:', error); setIsAdmin(false); }
  }, []);

  useEffect(() => {
    const appViewport = document.querySelector('.appViewport');
    if (!appViewport || isAdmin !== true) return undefined;
    appViewport.classList.add('admin-dashboard-active');
    return () => appViewport.classList.remove('admin-dashboard-active');
  }, [isAdmin]);

  if (isAdmin === null) return <EuiText>{translate('正在驗證管理員權限……')}</EuiText>;
  if (!isAdmin) return <EuiText color="danger">{translate('您沒有訪問此頁面的權限。請聯繫管理員。')}</EuiText>;

  const sideNav = [{
    name: translate('管理選項'),
    id: 'root',
    items: managementPages.map(item => ({ name: translate(item.name), id: item.id, isSelected: selectedPage === item.id, onClick: () => selectPage(item.id) })),
  }];

  const renderContent = () => {
    switch (selectedPage) {
      case 'users': return <UserManagement />;
      case 'songs': return <SongManagement />;
      case 'radio': return <RadioManagement />;
      case 'scripture': return <VerseManagement />;
      case 'mysql-scripture': return <BibleCuvManagement />;
      case 'reading-plans': return <ReadingPlanManagement />;
      case 'reading-plan-templates': return <ReadingPlanTemplateManagement />;
      case 'info': return <SermonManagement />;
      case 'network': return <NetworkManagement />;
      case 'prayer': return <PrayManagement />;
      case 'comment': return <CommentManagement />;
      case 'analysis': return <DataAnalysis />;
      case 'email-broadcasts': return <EmailBroadcastManagement />;
      default: return <div>{translate('請選擇一個管理選項。')}</div>;
    }
  };

  const isRadioManagement = selectedPage === 'radio';
  return <AdminLanguageBoundary language={language}>
    <div className="admin-dashboard-toolbar">
      <strong>{translate('管理後台')}</strong>
      <div className="admin-dashboard-toolbar-actions">
        <EuiButton size="s" color="text" iconType="home" onClick={() => navigate('/')}>{translate('首頁')}</EuiButton>
        <LanguageToggle />
      </div>
    </div>
    <nav className="admin-dashboard-mobile-nav" aria-label={translate('管理頁面導航')}>
      {managementPages.map(item => <EuiButton key={item.id} size="s" fill={selectedPage === item.id} color={selectedPage === item.id ? 'primary' : 'text'} onClick={() => selectPage(item.id)} className="admin-dashboard-mobile-nav-button">{translate(item.name)}</EuiButton>)}
    </nav>
    <EuiFlexGroup className="admin-dashboard-layout" gutterSize="none" responsive={false}>
      <EuiFlexItem grow={false} className="admin-dashboard-nav-item"><div className="admin-dashboard-side-nav"><EuiSideNav items={sideNav} mobileTitle={translate('管理導航')} style={{ width: 150 }} /></div></EuiFlexItem>
      <EuiFlexItem className="admin-dashboard-content-item"><EuiPanel className={['admin-dashboard-content-panel', isRadioManagement ? 'admin-dashboard-content-panel--radio' : ''].filter(Boolean).join(' ')} paddingSize={isRadioManagement ? 'none' : 'l'}>{renderContent()}</EuiPanel></EuiFlexItem>
    </EuiFlexGroup>
  </AdminLanguageBoundary>;
};

export default AdminDashboard;
